import { Select } from "@majitap/ui/arc/select/select";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { ACQUISITION_SOURCE, type AcquisitionSource } from "../../labels";
import { staffNamesQuery } from "../../team";

/** Radix Select has no empty value: "none" stands for not recorded. */
const NONE = "none";
const SOURCES = Object.keys(ACQUISITION_SOURCE) as AcquisitionSource[];

export interface Acquisition {
  source: AcquisitionSource | null;
  staffId: string | null;
}

/**
 * How a customer or business was won, and the staff member behind it (P15-14): who won the
 * customer, or who manages the business. Their orders' sales count for that staff member unless
 * staff place the order themselves.
 */
export function AcquisitionFields({
  value,
  onChange,
  staffLabel,
  staffHelp,
}: {
  value: Acquisition;
  onChange: (next: Acquisition) => void;
  staffLabel: string;
  staffHelp: string;
}): ReactNode {
  const staff = useQuery(staffNamesQuery);
  // deactivated staff can't be chosen, but one already set still shows
  const people = (staff.data ?? []).filter((s) => s.is_active || s.id === value.staffId);
  return (
    <>
      <Select
        label="How they were won"
        value={value.source ?? NONE}
        onValueChange={(v) => onChange({ ...value, source: v === NONE ? null : (v as AcquisitionSource) })}
        options={[
          { value: NONE, label: "Not recorded" },
          ...SOURCES.map((s) => ({ value: s, label: ACQUISITION_SOURCE[s] })),
        ]}
      />
      <Select
        label={staffLabel}
        description={staffHelp}
        value={value.staffId ?? NONE}
        disabled={staff.isPending}
        onValueChange={(v) => onChange({ ...value, staffId: v === NONE ? null : v })}
        options={[{ value: NONE, label: "Nobody" }, ...people.map((s) => ({ value: s.id, label: s.name }))]}
      />
    </>
  );
}

/** A staff member's name, for a record's details; null until the names load or when unset. */
export function useStaffName(id: string | null): string | null {
  const staff = useQuery({ ...staffNamesQuery, enabled: id !== null });
  return id ? (staff.data?.find((s) => s.id === id)?.name ?? null) : null;
}
